import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { settingsAPI } from '../utils/api';

// Define the settings interface based on the response structure
interface Settings {
  id: number;
  com_name: string;
  com_phone: string;
  com_address: string;
  user_id: number;
  update_date: string;
  firewall: string | null;
  skype: string;
  viber: string;
  logo_images: string;
  small_logo: string;
  favicon: string;
  com_email: string;
  is_features: number;
  panel_color_data: string;
  com_name_color: string;
  login_bg_color: string;
  login_header_color: string;
  switch_header_color: string;
  hasAdmin: boolean;
}

interface SettingsResponse {
  settings: Settings;
}

interface SettingsContextType {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await settingsAPI.getSettings();
      const settingsData: SettingsResponse = response.data;
      setSettings(settingsData.settings);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch settings');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async (): Promise<void> => {
    await fetchSettings();
  };

  // Function to apply color configuration from panel_color_data
  const applyColorConfiguration = (settings: Settings): void => {
    try {
      // Parse panel_color_data JSON string
      const panel_color_data = JSON.parse(settings.panel_color_data || "{}");

      // Extract color properties with fallback defaults (same as Vue.js implementation)
      const leftMenuBgColor = panel_color_data?.leftMenuBgColor || "#fff";
      const leftMenuTextColor = panel_color_data?.leftMenuTextColor || "rgba(0, 0, 0, 0.88)";
      const topNavBgColor = panel_color_data?.topNavBgColor || "rgb(255, 255, 255)";
      const topNavTextColor = panel_color_data?.topNavTextColor || "rgba(0, 0, 0, 0.88)";
      const panelBgColor = panel_color_data?.panelBgColor || "#FFFFFFFF";
      const panelTextColor = panel_color_data?.panelTextColor || "rgba(0, 0, 0, 0.88)";
      const layoutBgColor = panel_color_data?.layoutBgColor || "#f5f5f5";
      const layoutTextColor = panel_color_data?.layoutTextColor || "rgba(0, 0, 0, 0.88)";
      const buttonBgColor = panel_color_data?.buttonBgColor || "";
      const buttonTextColor = panel_color_data?.buttonTextColor || "";
      const loginPageCardBg = panel_color_data?.loginPageCardBg || "";

      // Remove existing dynamic styles if any
      const existingStyle = document.getElementById('kloud-dynamic-styles');
      if (existingStyle) {
        existingStyle.remove();
      }

      // Create and inject dynamic CSS styles (same as Vue.js implementation)
      const style = document.createElement('style');
      style.type = 'text/css';
      style.id = 'kloud-dynamic-styles';
      style.innerHTML = `
        .ant-layout .ant-layout-sider { background: ${leftMenuBgColor}; }
        .ant-menu-light { background: ${leftMenuBgColor}; color: ${leftMenuTextColor} }
        .ant-layout-header { background: ${topNavBgColor} !important; color: ${topNavTextColor}  }
        .ant-page-header .ant-page-header-heading-title {color: ${panelTextColor}}
        .ant-form-item .ant-form-item-label > label {color: ${panelTextColor}}
        .ant-layout { background: ${layoutBgColor}; }
        .ant-layout .ant-layout-footer { background: ${layoutBgColor}; color: ${layoutTextColor} }
        .ant-btn { ${buttonBgColor.length > 0 ? `background: ${buttonBgColor};` : ``} ${buttonTextColor.length > 0 ? `color: ${buttonTextColor};` : ``} }
        .ant-card { ${loginPageCardBg.length > 0 ? `background: ${loginPageCardBg};` : ``}; }
      `;

      document.head.appendChild(style);
    } catch (error) {
      console.error('Error applying color configuration:', error);
    }
  };

  // Apply color configuration when settings change
  useEffect(() => {
    if (!loading && settings) {
      applyColorConfiguration(settings);
    }
  }, [settings, loading]);

  // Fetch settings on full page load (not route changes)
  useEffect(() => {
    // Always fetch settings when the provider mounts (which happens on full page load)
    // This will be called only once when the app initializes, not on route changes
    fetchSettings();
  }, []);

  const contextValue: SettingsContextType = {
    settings,
    loading,
    error,
    fetchSettings,
    refreshSettings,
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use settings context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
